import { useDispatch } from 'react-redux';
import { axiosInstance } from '../../services/apiInterceptor';
import {
  REDIRECT_PERMISSION_MESSAGE_FOR_SWITCH_COMPANY,
  TENANT_DELETED_MESSAGE,
} from '../../constants/common';
import { setSwitchCredentials } from '../../pages/configuration/customerSwitchUserSlice';
import useInstance from './useInstance';

export type CascadeResult = {
  scope: 'companyMain' | 'home';
  message: string;
  confirmTitle: string;
  execute: () => void;
};

type CascadeTrigger = 'TENANT_DELETED' | 'PERMISSION_CHANGED';

export const OVERVIEW_PATHS = ['/', '/overview', '/check-user'];

function useCascadeRedirect() {
  const dispatch = useDispatch();
  const { getTenantById, getCurrentCid, pushTenant, getNextCid, clearCurrentTenant } =
    useInstance();

  /**
   * Returns null when the user should be silently redirected to overview
   * (no modal needed). Returns a CascadeResult when a modal must be shown.
   *
   * Cascade flow per scenario:
   *
   * Home → Company (Main):
   *   sub-page → overview (silent) → Home
   *
   * Home → own-company SB:
   *   sub-page → SB overview (silent) → Home
   *   (API returns userCompanyId === companyId → own company → skip to Home)
   *
   * Switch Company SB (direct or via Company):
   *   sub-page → SB overview (silent)
   *   → API /company/{companyId}/instances/main → redirect to Company Main
   *   → if Company Main also 403/deleted: overview (silent) → Home
   */
  const evaluateFallback = async (trigger: CascadeTrigger): Promise<CascadeResult | null> => {
    const currentCid = getCurrentCid();
    const tenant = getTenantById(currentCid);
    const pathname = window.location.pathname;
    // Include generic error shell so PERMISSION_CHANGED still runs sandbox → Main cascade (HTF-227).
    const isOnOverview = OVERVIEW_PATHS.includes(pathname) || pathname === '/server-maintainance';

    // Step 1: silent redirect to current instance's overview
    if (trigger === 'PERMISSION_CHANGED' && !isOnOverview) {
      return null;
    }

    const message =
      trigger === 'TENANT_DELETED'
        ? TENANT_DELETED_MESSAGE
        : REDIRECT_PERMISSION_MESSAGE_FOR_SWITCH_COMPANY;

    const companyId = tenant?.companyId;
    const instanceName = tenant?.instanceName;
    const isSandbox = instanceName && instanceName !== 'Main';

    // Step 2: for any sandbox, call the API to determine ownership and Main instance.
    // The API returns userCompanyId (logged-in user's home company) alongside
    // mainTenantInstanceId — no local storage flags needed.
    if (companyId && isSandbox) {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT}/api/identity/api/company/${companyId}/instances/main`
        );
        const data = response.data;
        const mainTenantInstanceId: string | undefined = data?.mainTenantInstanceId;
        const userCompanyId: string | undefined = data?.userCompanyId;

        // Own-company SB: user's home company matches the SB's company → go Home
        if (userCompanyId && userCompanyId === companyId) {
          // Fall through to Step 3
        } else {
          const isAlreadyOnMain =
            !mainTenantInstanceId ||
            mainTenantInstanceId === tenant?.instanceId ||
            mainTenantInstanceId === tenant?.tenantId;

          if (mainTenantInstanceId && !isAlreadyOnMain) {
            return {
              scope: 'companyMain',
              message,
              confirmTitle: 'Ok',
              execute: () => {
                dispatch(
                  setSwitchCredentials({
                    tenantId: mainTenantInstanceId,
                    isOrigin: '',
                  })
                );
                const newTenant = pushTenant({
                  cid: getNextCid(),
                  tenantId: mainTenantInstanceId,
                  instanceId: mainTenantInstanceId,
                  instanceName: 'Main',
                  roleId: '',
                  companyId,
                });
                window.location.href = `/#cid=${newTenant.cid}`;
              },
            };
          }
        }
      } catch {
        // API failed — fall through to home
      }
    }

    // Step 3: own-company SB, or "Main" instance, or Company Main also has
    // no permission, or API failed — go to home
    return {
      scope: 'home',
      message,
      confirmTitle: 'Ok',
      execute: () => clearCurrentTenant(),
    };
  };

  return { evaluateFallback };
}

export default useCascadeRedirect;
